"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { blogsApi } from "@/lib/api/services/blogs.api";
import { eventsApi } from "@/lib/api/services/events.api";
import { learningApi } from "@/lib/api/services/learning.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardMedia, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonArticle } from "@/components/ui/skeleton";
import { Plus, Clock, Calendar, BookOpen, ArrowRight, User, Sparkles } from "lucide-react";
import Link from "next/link";
import { useRequireAuth } from "@/hooks/use-auth-gate";
import { Pagination } from "@/components/shared/pagination";

interface Article {
  id: string;
  title: string;
  description: string;
  category: string;
  author: string;
  createdAt: string;
  readTime?: string;
  tags?: string[];
}

function ArticleCard({ article }: { article: Article }) {
  return (
    <Link href={`/blogs/${article.id}`}>
      <Card hover className="group flex h-full flex-col overflow-hidden">
        <CardMedia aspect="video" className="rounded-t-lg border-b border-border/50">
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 text-muted-foreground/20">
            <BookOpen className="h-12 w-12" />
          </div>
        </CardMedia>
        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2.5">
            {article.category && (
              <Badge variant="secondary" size="sm">{article.category}</Badge>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {article.readTime ?? "5 min read"}
            </span>
          </div>
          <h3 className="text-base font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed line-clamp-2 flex-1">
            {article.description}
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
            <User className="h-3 w-3" />
            <span>{article.author}</span>
            <span className="text-neutral-300">·</span>
            <span>{article.createdAt}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function BlogsPage() {
  const [page, setPage] = useState(1);
  const { requireAuth, AuthModal } = useRequireAuth("/blogs/write");

  const { data, isLoading } = useQuery({
    queryKey: ["blogs", page],
    queryFn: async () => {
      const [blogsData, eventData, courseData] = await Promise.all([
        blogsApi.list({ page, perPage: 20 }),
        eventsApi.list({ page: 1, perPage: 3, status: "upcoming" }),
        learningApi.listCourses({ page: 1, perPage: 3 }),
      ]);
      return {
        articles: ((blogsData as unknown as { data?: Article[] }).data ?? []) as Article[],
        meta: (blogsData as { meta?: { total: number; totalPages: number } }).meta ?? { total: 0, totalPages: 1 },
        relatedEvents: ((eventData as unknown as { data?: Array<{ id: string; title: string; date: string }> }).data ?? []),
        relatedCourses: ((courseData as unknown as { data?: Array<{ id: string; title: string; duration: string }> }).data ?? []),
      };
    },
  });

  const articles = data?.articles ?? [];
  const meta = data?.meta ?? { total: 0, totalPages: 1 };
  const relatedEvents = data?.relatedEvents ?? [];
  const relatedCourses = data?.relatedCourses ?? [];

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow flex items-center gap-2 mb-3">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            Blog
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground">
            Articles & Insights
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Thought leadership and perspectives from the HR community.
          </p>
        </div>
        <Button asChild size="sm" variant="premium">
          <Link href="/blogs/write" onClick={(e) => { if (!requireAuth()) e.preventDefault(); }}><Plus className="mr-1.5 h-4 w-4" /> Write Article</Link>
        </Button>
      </div>

      {/* Featured Article */}
      {!isLoading && featured && (
        <div className="mb-10">
          <Link href={`/blogs/${featured.id}`}>
            <Card hover className="group overflow-hidden">
              <div className="grid md:grid-cols-5">
                <CardMedia aspect="square" className="md:col-span-2 rounded-t-lg md:rounded-l-lg md:rounded-t-none border-b md:border-b-0 md:border-r border-border/50">
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 text-muted-foreground/20">
                    <BookOpen className="h-16 w-16" />
                  </div>
                </CardMedia>
                <div className="md:col-span-3 p-6 flex flex-col justify-center">
                  <Badge variant="premium" size="sm" dot className="mb-3 w-fit">Featured</Badge>
                  <h2 className="text-xl font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {featured.title}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                    {featured.description}
                  </p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><User className="h-3 w-3" />{featured.author}</span>
                    <span className="text-neutral-300">·</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{featured.readTime ?? "5 min read"}</span>
                    <span className="text-neutral-300">·</span>
                    <span>{featured.createdAt}</span>
                  </div>
                  <div className="mt-4">
                    <Button variant="ghost" size="sm" className="p-0 h-auto text-primary gap-1 hover:gap-2 transition-all">
                      Read article <ArrowRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        </div>
      )}

      {/* Article Grid */}
      {isLoading ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <SkeletonArticle key={i} shimmer />
          ))}
        </div>
      ) : rest.length === 0 && !featured ? (
        <Card variant="ghost" className="py-20">
          <CardContent className="text-center">
            <BookOpen className="mx-auto h-10 w-10 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground">No articles yet</h3>
            <p className="mt-1 text-sm text-muted-foreground">Be the first to share your thoughts.</p>
            <Button asChild className="mt-6" variant="premium">
              <Link href="/blogs/write" onClick={(e) => { if (!requireAuth()) e.preventDefault(); }}><Plus className="mr-1.5 h-4 w-4" /> Write the first article</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && rest.length > 0 && (
        <div className="mt-10">
          <Pagination page={page} totalPages={meta.totalPages} total={meta.total} onPageChange={setPage} />
        </div>
      )}

      {/* Related sidebar content */}
      {!isLoading && (relatedEvents.length > 0 || relatedCourses.length > 0) && (
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {relatedEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  Upcoming Events
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/60">
                  {relatedEvents.map((e) => (
                    <Link key={e.id} href={`/events/${e.id}`} className="flex items-center gap-3 p-3 transition-colors hover:bg-muted/10">
                      <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium text-foreground hover:text-primary transition-colors min-w-0 truncate">{e.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-auto">{e.date}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          {relatedCourses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-primary" />
                  Related Courses
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border/60">
                  {relatedCourses.map((c) => (
                    <Link key={c.id} href={`/learning/courses/${c.id}`} className="flex items-center gap-3 p-3 transition-colors hover:bg-muted/10">
                      <BookOpen className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium text-foreground hover:text-primary transition-colors min-w-0 truncate">{c.title}</span>
                      <span className="text-xs text-muted-foreground shrink-0 ml-auto">{c.duration}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    <AuthModal />
    </div>
  );
}
